import { useEffect, useRef, useState } from "react";
import PlatinumIcon from "../components/PlatinumIcon";

let nextId = 1;

function makeId() {
  return Date.now() * 1000 + (nextId++ % 1000);
}

const DEFAULT_POLL_INTERVAL = 10000;
const FETCH_STAGGER_MS = 2000;
const FETCH_STAGGER_OVER_5_MS = 5000;

function loadSavedWeapons() {
  try {
    const raw = localStorage.getItem("wf_weapons");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveWeapons(weapons) {
  try {
    localStorage.setItem(
      "wf_weapons",
      JSON.stringify(
        weapons.map(({ name, threshold, minThreshold, maxRolls, posFilters, negFilter, finalScore }) => ({
          name,
          threshold,
          minThreshold,
          maxRolls,
          posFilters,
          negFilter,
          finalScore,
        }))
      )
    );
  } catch {}
}

function loadInitialWeapons() {
  const saved = loadSavedWeapons();
  if (saved && Array.isArray(saved) && saved.length > 0) {
    return saved.map((w) => ({
      id: makeId(),
      name: w.name ?? "stug",
      threshold: w.threshold ?? "",
      minThreshold: w.minThreshold ?? "",
      maxRolls: w.maxRolls ?? "",
      posFilters: Array.isArray(w.posFilters)
        ? w.posFilters
        : w.posFilter
        ? [w.posFilter]
        : [],
      negFilter: w.negFilter ?? "",
      finalScore: w.finalScore ?? "",
    }));
  }
  return [{ id: makeId(), name: "stug", threshold: "", minThreshold: "", maxRolls: "", posFilters: [], negFilter: "", finalScore: "" }];
}

function WeaponSummary({ displayName, weaponSummaries, setWeaponSummaries, rivenAttributes }) {
  const [summary, setSummary] = useState(() => weaponSummaries[displayName] || null);
  useEffect(() => {
    let mounted = true;
    if (!displayName) return;
    setSummary(weaponSummaries[displayName] || null);
    if (weaponSummaries[displayName]) {
      setSummary(weaponSummaries[displayName]);
      return;
    }
    fetch(`/.netlify/functions/weaponSummary?weapon=${encodeURIComponent(displayName)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!mounted || !data) return;
        setWeaponSummaries((prev) => ({ ...prev, [displayName]: data }));
        setSummary(data);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [displayName, weaponSummaries, setWeaponSummaries]);

  if (!summary) return <div className="text-xs text-slate-500">Sem dados</div>;

  const mapName = (u) => {
    if (!u) return u;
    if (rivenAttributes[u]) return rivenAttributes[u];
    const synonyms = {
      combo_efficiency: "Heavy Attack Efficiency",
      slide_attack_critical_chance: "Critical Chance for Slide Attack",
      initial_combo: "Initial Combo",
    };
    if (synonyms[u]) return synonyms[u];

    // Fallback: show readable text if API does not expose this exact url_name.
    return String(u)
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  };
  const goodRolls = (summary.goodRolls && summary.goodRolls.length > 0)
    ? summary.goodRolls
    : ((summary.mandatory || summary.optional)
      ? [{ mandatory: summary.mandatory || [], optional: summary.optional || [] }]
      : []);
  const badAccepted = summary.accepted || [];

  return (
    <div className="text-xs text-slate-700 p-2">
      {goodRolls.length <= 1 ? (
        <>
          <div className="font-semibold">Mandatório:</div>
          <div className="mb-1">{((goodRolls[0]?.mandatory) || []).map(mapName).join(', ') || '—'}</div>

          <div className="font-semibold">Aceites:</div>
          <div className="mb-1">{((goodRolls[0]?.optional) || []).map(mapName).join(', ') || '—'}</div>

          <div className="font-semibold">Maus atributos aceites:</div>
          <div>{badAccepted.map(mapName).join(', ') || '—'}</div>
        </>
      ) : (
        <>
          {goodRolls.map((c, idx) => (
            <div key={idx} className={idx > 0 ? "mt-2 pt-2 border-t border-slate-200" : ""}>
              <div className="font-semibold text-[11px] mb-0.5">Caso {idx + 1}</div>

              <div className="font-semibold">Mandatório:</div>
              <div className="mb-1">{(c.mandatory || []).map(mapName).join(', ') || '—'}</div>

              <div className="font-semibold">Aceites:</div>
              <div className="mb-1">{(c.optional || []).map(mapName).join(', ') || '—'}</div>

              <div className="font-semibold">Maus atributos aceites:</div>
              <div>{badAccepted.map(mapName).join(', ') || '—'}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default function SniperApp() {
  const [weapons, setWeapons] = useState(loadInitialWeapons);
  const [weaponOptions, setWeaponOptions] = useState([]);
  const [rivenAttributes, setRivenAttributes] = useState({});
  const [rivenAttributeUnits, setRivenAttributeUnits] = useState({});
  const [auctionData, setAuctionData] = useState({});
  const [weaponSummaries, setWeaponSummaries] = useState({});
  const [rateLimitCooldown, setRateLimitCooldown] = useState(null);
  const [rateLimitSecs, setRateLimitSecs] = useState(0);
  const [countdown, setCountdown] = useState(DEFAULT_POLL_INTERVAL / 1000);
  const [notifPermission, setNotifPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [paused, setPaused] = useState(false);
  const [pollInterval, setPollInterval] = useState(() =>
    Number(localStorage.getItem("wf_poll_interval") || DEFAULT_POLL_INTERVAL)
  );
  const [copiedId, setCopiedId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [myUsername, setMyUsername] = useState(
    () => localStorage.getItem("wf_my_username") || ""
  );

  const seenIdsRef = useRef({});
  const firstFetchDoneRef = useRef({});
  const audioCtxRef = useRef(null);
  const dragSrcIdRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);
  const rateLimitedUntilRef = useRef(null);
  const weaponsRef = useRef(weapons);
  const nextFetchAtRef = useRef(Date.now() + DEFAULT_POLL_INTERVAL);
  const pausedRef = useRef(false);
  const pollIntervalRef = useRef(Number(localStorage.getItem("wf_poll_interval") || DEFAULT_POLL_INTERVAL));
  const scheduleRef = useRef(null);
  const saveDebounceRef = useRef(null);

  useEffect(() => {
    fetch("/v1/riven/items")
      .then((r) => r.json())
      .then((data) => {
        const items = data?.payload?.items ?? [];
        setWeaponOptions([...items].sort((a, b) => a.item_name.localeCompare(b.item_name)));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetch("/v1/riven/attributes")
      .then((r) => r.json())
      .then((data) => {
        const attrs = data?.payload?.attributes ?? [];
        const nameMap = {};
        const unitsMap = {};
        attrs.forEach((a) => {
          nameMap[a.url_name] = a.effect;
          unitsMap[a.url_name] = a.units;
        });
        setRivenAttributes(nameMap);
        setRivenAttributeUnits(unitsMap);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const initAudio = () => {
      if (!audioCtxRef.current)
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    };
    window.addEventListener("click", initAudio, { once: true });
    window.addEventListener("keydown", initAudio, { once: true });
    return () => {
      window.removeEventListener("click", initAudio);
      window.removeEventListener("keydown", initAudio);
    };
  }, []);

  useEffect(() => { weaponsRef.current = weapons; }, [weapons]);
  useEffect(() => {
    clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => saveWeapons(weapons), 500);
  }, [weapons]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => {
    pollIntervalRef.current = pollInterval;
    localStorage.setItem("wf_poll_interval", String(pollInterval));
  }, [pollInterval]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(Math.max(0, Math.ceil((nextFetchAtRef.current - Date.now()) / 1000)));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!rateLimitCooldown) return;
    const until = rateLimitCooldown;
    const update = () => {
      const rem = Math.ceil((until - Date.now()) / 1000);
      if (rem <= 0) {
        setRateLimitSecs(0);
        setRateLimitCooldown(null);
        rateLimitedUntilRef.current = null;
        return true;
      }
      setRateLimitSecs(rem);
      return false;
    };
    update();
    const iv = setInterval(() => {
      if (update()) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [rateLimitCooldown]);

  const playSound = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  };

  const showNotification = (displayName, count) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try {
      new Notification(`⚡ ${count} novo(s) riven: ${displayName}`, {
        body: `Encontrado(s) ${count} leilão(ões) abaixo do threshold`,
        icon: "/Platinum64.webp",
        tag: `wf-riven-${displayName}`,
      });
    } catch {}
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setNotifPermission(result);
  };

  const copyTradeMessage = (ingameName, weaponName, itemName, price) => {
    if (!ingameName) return;
    navigator.clipboard.writeText(`/w ${ingameName} Hi, WTB ${weaponName} ${itemName} ${price}p. Is it still available? (https://www.rivenappraiser.site/)`);
    setCopiedId(ingameName);
    setTimeout(() => setCopiedId((prev) => (prev === ingameName ? null : prev)), 2000);
  };

  const getWeaponDisplayName = (urlName) =>
    weaponOptions.find((o) => o.url_name === urlName)?.item_name ?? urlName;

  const statusColor = (status) => {
    if (status === "ingame") return "bg-amber-500";
    if (status === "online") return "bg-emerald-600";
    return "bg-slate-400";
  };

  const statusLabel = (status) => {
    if (status === "ingame") return "In Game";
    if (status === "online") return "Online";
    return "Offline";
  };

  const formatAttributeValue = (attr) => {
    const units = rivenAttributeUnits[attr.url_name];
    const numericValue = typeof attr.value === "number" ? attr.value : Number(attr.value);
    if (units === "multiply" && Number.isFinite(numericValue)) {
      return `x${Math.abs(numericValue).toFixed(2)}`;
    }
    const shown = Number.isFinite(numericValue)
      ? numericValue.toFixed(1)
      : attr.value;
    // Only append % if units explicitly says "percent"; null/undefined means just the number
    return units === "percent" ? `${shown}%` : shown;
  };

  const fetchWeapon = async ({ id, name, threshold, minThreshold, maxRolls, posFilters, negFilter, finalScore }) => {
    if (!weaponsRef.current.some((w) => w.id === id)) return;
    if (rateLimitedUntilRef.current && Date.now() < rateLimitedUntilRef.current) return;
    setAuctionData((prev) => ({
      ...prev,
      [id]: {
        auctions: prev[id]?.auctions ?? [],
        newIds: prev[id]?.newIds ?? new Set(),
        loading: true,
        lowestPrice: prev[id]?.lowestPrice ?? null,
        lowestOfflinePrice: prev[id]?.lowestOfflinePrice ?? null,
      },
    }));

    try {
      const payload = {
        weaponName: name,
        displayName: getWeaponDisplayName(name),
        threshold: threshold ?? null,
        minThreshold: minThreshold ?? null,
        maxRolls: maxRolls ?? null,
        posFilters: posFilters ?? [],
        negFilter: negFilter ?? null,
        finalScore: finalScore ?? null,
      };

      const res = await fetch('/.netlify/functions/sniper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 429) {
        const until = Date.now() + 60_000;
        rateLimitedUntilRef.current = until;
        setRateLimitCooldown(until);
        setRateLimitSecs(60);
        return;
      }

      const data = await res.json();
      const finalFiltered = data?.auctions ?? [];
      const finalLowestPrice = data?.lowestPrice ?? null;
      const finalLowestOfflinePrice = data?.lowestOfflinePrice ?? null;

      if (!seenIdsRef.current[id]) seenIdsRef.current[id] = new Set();
      const newIds = new Set(finalFiltered.filter((a) => !seenIdsRef.current[id].has(a.id)).map((a) => a.id));
      finalFiltered.forEach((a) => seenIdsRef.current[id].add(a.id));

      if (!weaponsRef.current.some((w) => w.id === id)) return;
      if (newIds.size > 0 && firstFetchDoneRef.current[id]) {
        playSound();
        showNotification(getWeaponDisplayName(name), newIds.size);
      }
      firstFetchDoneRef.current[id] = true;

      setAuctionData((prev) => ({
        ...prev,
        [id]: { auctions: finalFiltered, loading: false, newIds, lowestPrice: finalLowestPrice, lowestOfflinePrice: finalLowestOfflinePrice },
      }));
    } catch (err) {
      console.error(err);
      setAuctionData((prev) => ({
        ...prev,
        [id]: {
          auctions: prev[id]?.auctions ?? [],
          loading: false,
          newIds: new Set(),
          lowestPrice: prev[id]?.lowestPrice ?? null,
          lowestOfflinePrice: prev[id]?.lowestOfflinePrice ?? null,
        },
      }));
    }
  };

  const fetchWeaponRef = useRef(null);
  fetchWeaponRef.current = fetchWeapon;

  const fetchAllSequential = (list) => {
    const staggerMs = list.length > 5 ? FETCH_STAGGER_OVER_5_MS : FETCH_STAGGER_MS;
    return list.map((w, i) => setTimeout(() => fetchWeaponRef.current(w), i * staggerMs));
  };

  const fetchAllSeqRef = useRef(null);
  fetchAllSeqRef.current = fetchAllSequential;

  const pollTimerRef = useRef(null);
  const activeFetchIdsRef = useRef([]);

  useEffect(() => {
    const schedule = () => {
      if (pausedRef.current) return;
      const ei = pollIntervalRef.current * Math.max(1, weaponsRef.current.length);
      nextFetchAtRef.current = Date.now() + ei;
      activeFetchIdsRef.current = fetchAllSeqRef.current(weaponsRef.current);
      pollTimerRef.current = setTimeout(schedule, ei);
    };
    scheduleRef.current = schedule;

    schedule();

    return () => {
      activeFetchIdsRef.current.forEach(clearTimeout);
      clearTimeout(pollTimerRef.current);
    };
  }, []);

  const addWeapon = () =>
    setWeapons((prev) => [
      ...prev,
      { id: makeId(), name: "torid", threshold: "", minThreshold: "", maxRolls: "", posFilters: [], negFilter: "", finalScore: "" },
    ]);

  const addPosFilter = (id) =>
    setWeapons((prev) =>
      prev.map((w) =>
        w.id === id && w.posFilters.length < 3
          ? { ...w, posFilters: [...w.posFilters, ""] }
          : w
      )
    );

  const updatePosFilter = (id, index, value) =>
    setWeapons((prev) =>
      prev.map((w) =>
        w.id === id
          ? {
              ...w,
              posFilters: w.posFilters.map((v, i) => (i === index ? value : v)),
            }
          : w
      )
    );

  const removePosFilter = (id, index) =>
    setWeapons((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, posFilters: w.posFilters.filter((_, i) => i !== index) }
          : w
      )
    );

  const removeWeapon = (id) => {
    setWeapons((prev) => prev.filter((w) => w.id !== id));
    setAuctionData((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    delete seenIdsRef.current[id];
    delete firstFetchDoneRef.current[id];
  };

  const updateWeapon = (id, field, value) =>
    setWeapons((prev) =>
      prev.map((w) => (w.id === id ? { ...w, [field]: value } : w))
    );

  const saveUsername = (val) => {
    setMyUsername(val);
    localStorage.setItem("wf_my_username", val);
  };

  const sortedAttributes = Object.entries(rivenAttributes).sort(([, a], [, b]) =>
    a.localeCompare(b)
  );

  return (
    <div className="min-h-screen bg-transparent text-slate-100 flex flex-col" onClick={(e) => { if (sidebarOpen && e.target === e.currentTarget) setSidebarOpen(false); }}>
      {rateLimitCooldown && (
        <div className="bg-amber-600 text-white text-sm text-center px-4 py-2 font-semibold shadow-sm">
          ⚠️ Demasiados pedidos — a retomar em <span className="font-bold">{rateLimitSecs}s</span>...
        </div>
      )}

      <header className="bg-slate-950/95 text-slate-50 px-6 py-4 flex items-center justify-between shadow-lg shadow-slate-900/10 gap-4 border-b border-sky-500/20 backdrop-blur">
        <h1 className="text-lg font-bold tracking-wide">⚡ Warframe Riven Sniper</h1>
        <div className="flex items-center gap-3 text-sm flex-wrap justify-end">
          <span className="text-slate-300 text-xs font-medium">
            {paused ? (
              <span className="text-amber-300 font-semibold">⏸ Pausado</span>
            ) : (
              <>Próximo refresh:{" "}<span className="text-slate-50 font-semibold">{countdown}s</span></>
            )}
          </span>

          <button
            onClick={addWeapon}
            className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition shadow-sm shadow-sky-900/20"
          >
            + Adicionar arma
          </button>
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="flex flex-col justify-center gap-1 w-7 h-7 flex-shrink-0"
            title="Opções"
          >
            <span className="block w-5 h-0.5 bg-white mx-auto"></span>
            <span className="block w-5 h-0.5 bg-white mx-auto"></span>
            <span className="block w-5 h-0.5 bg-white mx-auto"></span>
          </button>
        </div>
      </header>

      <div
        className={`fixed top-0 right-0 h-full w-72 bg-slate-950 shadow-2xl z-50 flex flex-col transition-transform duration-300 border-l border-slate-800 ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-slate-50">
          <span className="font-semibold text-sm">Opções</span>
          <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-slate-100 text-lg leading-none">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">O meu username</label>
            <input
              type="text"
              value={myUsername}
              onChange={(e) => saveUsername(e.target.value)}
              placeholder="NomeNoJogo"
              className="w-full border border-slate-700 rounded-lg px-3 py-2 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100 placeholder:text-slate-500"
            />
            {myUsername && (
              <p className="text-xs text-amber-300 mt-1 font-medium">Os teus leilões aparecem a laranja.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Notificações</label>
            {notifPermission === "granted" ? (
              <span className="text-emerald-300 text-sm font-semibold">🔔 Ativas</span>
            ) : (
              typeof Notification !== "undefined" && (
                <button
                  onClick={requestNotifPermission}
                  className="w-full bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-3 py-2 rounded-lg transition shadow-sm"
                >
                  🔔 Ativar notificações
                </button>
              )
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Intervalo de refresh</label>
            <select
              value={pollInterval}
              onChange={(e) => setPollInterval(Number(e.target.value))}
              className="w-full border border-slate-700 rounded-lg px-3 py-2 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
            >
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={15000}>15s</option>
              <option value={30000}>30s</option>
              <option value={60000}>60s</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Polling</label>
            <button
              onClick={() => {
                const next = !paused;
                setPaused(next);
                if (!next) {
                  clearTimeout(pollTimerRef.current);
                  scheduleRef.current?.();
                }
              }}
              className={`w-full text-sm px-3 py-1.5 rounded-lg transition font-medium ${
                paused
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                  : "bg-slate-200 hover:bg-slate-300 text-slate-800"
              }`}
            >
              {paused ? "▶ Retomar" : "⏸ Pausar"}
            </button>
          </div>
        </div>
      </div>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {weapons.map((w) => {
            const data = auctionData[w.id] ?? { auctions: [], loading: false, newIds: new Set() };
            const isDragOver = dragOverId === w.id && dragSrcIdRef.current !== w.id;
            return (
              <div
                key={w.id}
                draggable
                onDragStart={() => { dragSrcIdRef.current = w.id; }}
                onDragOver={(e) => { e.preventDefault(); if (dragSrcIdRef.current !== w.id) setDragOverId(w.id); }}
                onDragLeave={() => setDragOverId(null)}
                onDrop={() => {
                  const srcId = dragSrcIdRef.current;
                  const dstId = w.id;
                  if (srcId && srcId !== dstId) {
                    setWeapons((prev) => {
                      const next = [...prev];
                      const srcIdx = next.findIndex((x) => x.id === srcId);
                      const dstIdx = next.findIndex((x) => x.id === dstId);
                      if (srcIdx === -1 || dstIdx === -1) return prev;
                      const [moved] = next.splice(srcIdx, 1);
                      next.splice(dstIdx, 0, moved);
                      return next;
                    });
                  }
                  dragSrcIdRef.current = null;
                  setDragOverId(null);
                }}
                onDragEnd={() => { dragSrcIdRef.current = null; setDragOverId(null); }}
                className={`bg-slate-950/90 rounded-2xl shadow-lg shadow-slate-950/40 border flex flex-col backdrop-blur cursor-grab active:cursor-grabbing transition-shadow ${
                  isDragOver
                    ? "border-sky-400 ring-2 ring-sky-300 shadow-sky-200"
                    : data.newIds?.size > 0
                    ? "border-green-500 ring-2 ring-green-400"
                    : "border-slate-800/80"
                }`}
                style={{ maxHeight: "calc(100vh - 90px)" }}
              >
                <div className="p-4 border-b border-slate-800 bg-slate-900/90 rounded-t-2xl flex flex-col gap-3 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <select
                      value={w.name}
                      onChange={(e) => updateWeapon(w.id, "name", e.target.value)}
                      className="border border-slate-700 rounded-lg px-2 py-1.5 text-sm flex-1 bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
                    >
                      {weaponOptions.map((opt) => (
                        <option key={opt.url_name} value={opt.url_name}>{opt.item_name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeWeapon(w.id)}
                      className="text-red-400 hover:text-red-600 font-bold text-base leading-none flex-shrink-0"
                    >✕</button>
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-slate-400 mb-1 uppercase tracking-wide">
                        <span className="inline-flex items-center gap-1">
                          Mín.
                          <PlatinumIcon className="w-3.5 h-3.5" />
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Qualquer"
                        value={w.minThreshold}
                        onChange={(e) => updateWeapon(w.id, "minThreshold", e.target.value)}
                        className="w-full border border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100 placeholder:text-slate-500"
                      />

                      <label className="block text-xs font-semibold text-slate-400 mb-1 mt-2 uppercase tracking-wide">Final score mín.</label>
                      <input
                        type="number"
                        placeholder="Qualquer"
                        value={w.finalScore}
                        onChange={(e) => updateWeapon(w.id, "finalScore", e.target.value)}
                        className="w-full border border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <div className="flex-1">

                      <label className="block text-xs font-semibold text-slate-400 mb-1 uppercase tracking-wide">
                        <span className="inline-flex items-center gap-1">
                          Máx.
                          <PlatinumIcon className="w-3.5 h-3.5" />
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={w.threshold}
                        onChange={(e) => updateWeapon(w.id, "threshold", e.target.value)}
                        className="w-full border border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100 placeholder:text-slate-500"
                      />

                      <label className="block text-xs font-semibold text-slate-400 mb-1 mt-2 uppercase tracking-wide">Máx. rolls</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="—"
                        value={w.maxRolls}
                        onChange={(e) => updateWeapon(w.id, "maxRolls", e.target.value)}
                        className="w-full border border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <div className="relative group">
                        <button
                          type="button"
                          aria-label="Mostrar melhores e piores atributos"
                          className="text-slate-500 hover:text-slate-300 w-6 h-6 rounded-full flex items-center justify-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-8 5a1 1 0 100-2 1 1 0 000 2zm1-8a1 1 0 00-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
                          </svg>
                        </button>

                        <div className="hidden group-hover:block absolute z-50 w-64 p-2 bg-slate-950 border border-slate-700 rounded shadow-lg text-xs text-slate-300 -left-2 -top-40">
                          <WeaponSummary key={w.name} displayName={getWeaponDisplayName(w.name)} weaponSummaries={weaponSummaries} setWeaponSummaries={setWeaponSummaries} rivenAttributes={rivenAttributes} />
                        </div>
                      </div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide">Atributos +</label>
{w.posFilters.length < 3 && (
                      <button
                        onClick={() => addPosFilter(w.id)}
                        aria-label="Adicionar atributo"
                        title="Adicionar atributo"
                        className="ml-auto inline-flex items-center justify-center w-4 h-4 rounded-full bg-sky-600 hover:bg-sky-700 text-white transition-shadow shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                       
                    </div>
                    {w.posFilters.map((pf, idx) => (
                      <div key={idx} className="flex items-center gap-1 mb-1">
                        <select
                          value={pf}
                          onChange={(e) => updatePosFilter(w.id, idx, e.target.value)}
                          className="flex-1 border border-slate-700 rounded-lg px-2 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
                        >
                          <option value="">Qualquer</option>
                          {sortedAttributes.map(([url, label]) => (
                            <option key={url} value={url}>{label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => removePosFilter(w.id, idx)}
                          className="text-red-400 hover:text-red-600 text-xs font-bold leading-none"
                        >✕</button>
                      </div>
                    ))}
                    
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1 uppercase tracking-wide">Atributo −</label>
                    <select
                      value={w.negFilter}
                      onChange={(e) => updateWeapon(w.id, "negFilter", e.target.value)}
                      className="w-full border border-slate-700 rounded-lg px-2 py-1.5 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
                    >
                      <option value="">Qualquer</option>
                      <option value="any">— Tem negativo —</option>
                      {sortedAttributes.map(([url, label]) => (
                        <option key={url} value={url}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-400 font-medium">
                    <span className="flex items-center gap-3">
                      <span>
                        Menor:{" "}
                        <span className="font-semibold text-slate-100">
                          {data.lowestPrice != null ? (
                            <span className="inline-flex items-center gap-1">
                              {data.lowestPrice}
                              <PlatinumIcon className="w-3.5 h-3.5" />
                            </span>
                          ) : "—"}
                        </span>
                      </span>
                      <span className="text-slate-600">|</span>
                      <span>
                        Min offline:{" "}
                        <span className="font-semibold text-slate-300">
                          {data.lowestOfflinePrice != null ? (
                            <span className="inline-flex items-center gap-1">
                              {data.lowestOfflinePrice}
                              <PlatinumIcon className="w-3.5 h-3.5" />
                            </span>
                          ) : "—"}
                        </span>
                      </span>
                    </span>
                    {data.loading && <span className="text-sky-600 animate-pulse font-semibold">A carregar...</span>}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {!data.loading && data.auctions.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4 font-medium">Nenhum leilão online.</p>
                  )}
                  <ul className="space-y-2">
                    {data.auctions.map((auction) => {
                      const isNew = data.newIds?.has(auction.id);
                      const isOwn = myUsername.trim() !== "" && auction.owner?.ingame_name?.toLowerCase() === myUsername.trim().toLowerCase();
                      const attrs = auction.item?.attributes ?? [];
                      const weaponDisplayName = getWeaponDisplayName(w.name);
                      const evalResult = auction.evaluation ?? { score: 0, label: '', badgeClass: '' };
                      const bestCaseIndex = Number.isInteger(evalResult.bestCaseIndex) ? evalResult.bestCaseIndex : 0;
                      const caseCeilings = Array.isArray(evalResult.caseCeilings) ? evalResult.caseCeilings : [];
                      const bestCaseCeiling = Number(caseCeilings[bestCaseIndex]);
                      const numericScore = Number(evalResult.score);
                      const showGoldStar =
                        Number.isFinite(numericScore) &&
                        Number.isFinite(bestCaseCeiling) &&
                        numericScore === bestCaseCeiling &&
                        (bestCaseCeiling === 9 || bestCaseCeiling === 11);
                      return (
                        <li
                          key={auction.id}
                          className={`rounded-xl p-3 text-sm leading-normal border ${
                            isOwn
                              ? "bg-orange-950/60 border-orange-800"
                              : isNew
                                ? "bg-green-950/60 border-green-800"
                                : "bg-slate-900 border-slate-700"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-slate-100">
                              <span className="inline-flex items-center gap-1">
                                {auction.buyout_price ?? "—"}
                                <PlatinumIcon className="w-3.5 h-3.5" />
                                <span className={`text-[10px] font-bold rounded px-1 py-0.5 ${evalResult.badgeClass}`}>
                                            {evalResult.score > 0 ? `${evalResult.score}+` : evalResult.score} {evalResult.label}
                                            {(evalResult && Array.isArray(evalResult.caseScores) && evalResult.caseScores.length > 1) && (
                                              <span className="ml-1 text-[10px] font-medium">Caso {((evalResult.bestCaseIndex ?? 0) + 1)}</span>
                                            )}
                                            {showGoldStar && (
                                              <img src="/gold-star.avif" alt="Gold Star" className="inline w-4 h-4 align-middle ml-1" />
                                            )}
                                          </span>
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <span className={`inline-block w-2 h-2 rounded-full ${statusColor(auction.owner?.status)}`} />
                              <span className="text-xs text-slate-400 font-medium">{statusLabel(auction.owner?.status)}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 ">
                            <span className="flex ">
                              <button
                                onClick={() => copyTradeMessage(
                                  auction.owner?.ingame_name,
                                  weaponDisplayName,
                                  auction.item?.name ?? "riven",
                                  auction.buyout_price
                                )}
                                className="text-xs leading-normal text-slate-200 hover:text-sky-300 truncate text-left w-full"
                                title="Copiar mensagem de trade"
                                >
                                {copiedId === auction.owner?.ingame_name
                                  ? "✓ Copiado!"
                                  : auction.owner?.ingame_name}
                              </button>
                            </span>
                            <span className="flex ml-auto">
                              <a
                                href={`https://warframe.market/auction/${auction.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-block text-xs font-semibold text-sky-300 hover:underline"
                              >
                                Ver leilão ↗
                              </a>
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mb-2"> 
                            <span className="flex ">
                              <div className=" text-xs leading-normal text-slate-400">Rolls: {auction.item?.re_rolls ?? 0}</div>
                            </span>
                            <span className="flex ">
                              <div className=" text-xs leading-normal text-slate-400">Rank: {auction.item?.mod_rank ?? 0}</div>
                            </span>
                          </div>
                          {attrs.length > 0 && (
                            <div className="flex flex-col gap-0.5 mt-1 mb-1">
                              {attrs.map((attr, i) => {
                                const tier = attr.tier ?? null;
                                const grade = attr.grade ?? "N/A";

                                return (
                                  <div
                                    key={i}
                                    className={`flex items-center justify-between text-xs px-1.5 py-0.5 rounded ${
                                      attr.positive ? "bg-green-950/60 text-green-200" : "bg-red-950/60 text-red-200"
                                    }`}
                                  >
                                    <span className="flex items-center gap-1 flex-wrap">
                                      <span className={`font-bold text-[10px] rounded px-1 ${grade === "N/A" ? "text-slate-400 bg-slate-800" : "text-slate-200 bg-slate-700"}`}>
                                        {grade}
                                      </span>
                                      <span>{rivenAttributes[attr.url_name] ?? attr.url_name}</span>
                                      <span>
                                        {rivenAttributeUnits[attr.url_name] === "multiply" ? "" : (attr.positive ? "+" : "")}
                                        {formatAttributeValue(attr)}
                                      </span>
                                    </span>
                                    {tier === "S" && (
                                      <span className="ml-1 font-bold text-[10px] bg-amber-900/60 text-amber-200 rounded px-1">++</span>
                                    )}
                                    {tier === "A" && (
                                      <span className="ml-1 font-bold text-[10px] bg-sky-900/60 text-sky-200 rounded px-1">+</span>
                                    )}
                                    {tier === "ok" && attr.positive === false && (
                                      <span className="ml-1 font-bold text-[10px] bg-red-900/60 text-red-200 rounded px-1">++</span>
                                    )}
                                    {tier === "badA" && attr.positive === false && (
                                      <span className="ml-1 font-bold text-[10px] bg-orange-900/60 text-orange-200 rounded px-1">-</span>
                                    )}
                                    {tier === "badS" && attr.positive === false && (
                                      <span className="ml-1 font-bold text-[10px] bg-rose-900/60 text-rose-200 rounded px-1">--</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
